// Order documents — the PDFs the backend renders for one order (label, packing
// slip). `label` follows Printing Setup: with the "4x6 + Packing Slip" layout
// the PDF carries the carrier label and then the packing slip.
//
// No new tab is ever opened for a document (Volkan 2026-10-27): a tab reserved
// during the click stayed blank and looked like an error. Printing draws the
// PDF's pages as images (`pdf-print.ts`) into a hidden same-origin frame and
// opens its print dialog over the app — one dialog for a whole bulk run. A PDF
// loaded straight into a frame cannot be printed: Chrome treats it as another
// origin. When drawing fails, the file is downloaded instead.

import { apiUrl } from '../../lib/api'
import { renderPdfPages, type RenderedPage } from './pdf-print'

export type OrderDocumentType = 'label' | 'packing_slip'
/** What the click asked for: the print dialog, or just the file. */
export type DocumentAction = 'print' | 'download'
/** What really happened: a document that cannot be drawn is downloaded instead. */
export type DeliveredAs = 'printing' | 'downloaded'
export type OpenDocumentResult = DeliveredAs | 'no_label' | 'failed'

let printFrame: HTMLIFrameElement | null = null
let printImageUrls: string[] = []

function download(pdf: Blob, filename: string): void {
  const url = URL.createObjectURL(pdf)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  // The browser has the file once the click is handled; a minute is plenty.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function clearPrintFrame(): void {
  printFrame?.remove()
  printFrame = null
  printImageUrls.forEach((url) => URL.revokeObjectURL(url))
  printImageUrls = []
}

function pageMarkup(pages: RenderedPage[], urls: string[]): string {
  const first = pages[0]
  const images = pages
    .map((p, i) => `<img src="${urls[i]}" style="width:${p.widthIn}in;height:${p.heightIn}in" alt="">`)
    .join('')
  // One printed sheet per page, sized like the PDF (a 4x6 label stays 4x6).
  return (
    '<!doctype html><html><head><meta charset="utf-8"><style>' +
    `@page{size:${first.widthIn}in ${first.heightIn}in;margin:0}` +
    'html,body{margin:0;padding:0;background:#fff}' +
    'img{display:block;break-after:page}img:last-child{break-after:auto}' +
    `</style></head><body>${images}</body></html>`
  )
}

async function printPages(pages: RenderedPage[]): Promise<void> {
  clearPrintFrame()
  const urls = pages.map((p) => URL.createObjectURL(p.image))
  printImageUrls = urls
  // Offscreen, not hidden: a frame that is not rendered prints blank.
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.tabIndex = -1
  frame.style.position = 'fixed'
  frame.style.left = '-10000px'
  frame.style.top = '0'
  frame.style.width = '4in'
  frame.style.height = '6in'
  frame.style.border = '0'
  const loaded = new Promise<void>((resolve) => {
    frame.onload = () => resolve()
  })
  // srcdoc keeps the frame same-origin, so the page may print it.
  frame.srcdoc = pageMarkup(pages, urls)
  document.body.appendChild(frame)
  printFrame = frame
  await loaded
  const doc = frame.contentDocument
  if (!doc || !frame.contentWindow) throw new Error('print frame unavailable')
  // Every page image decoded first, or the dialog shows blank sheets.
  await Promise.all(Array.from(doc.querySelectorAll('img'), (img) => img.decode()))
  frame.contentWindow.focus()
  frame.contentWindow.print()
}

/** Print or download a rendered PDF. Printing falls back to a download if the PDF cannot be drawn. */
export async function deliverDocument(
  pdf: Blob,
  filename: string,
  action: DocumentAction,
): Promise<DeliveredAs> {
  if (action === 'print') {
    try {
      const pages = await renderPdfPages(pdf)
      if (pages.length > 0) {
        await printPages(pages)
        return 'printing'
      }
    } catch {
      // Fall through: the file still gets out.
    }
  }
  download(pdf, filename)
  return 'downloaded'
}

/** The carrier's own label file lives on another site: it can only be opened. */
function openExternal(url: string): void {
  if (window.open(url, '_blank', 'noopener')) return
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  link.click()
}

export async function openOrderDocument(
  orderId: string,
  type: OrderDocumentType,
  action: DocumentAction = 'print',
  fallbackUrl: string | null = null,
): Promise<OpenDocumentResult> {
  // When the document cannot be served, the carrier's own label file is
  // offered instead of nothing.
  const giveUp = () => {
    if (fallbackUrl) openExternal(fallbackUrl)
  }
  let res: Response
  try {
    res = await fetch(apiUrl(`/admin/orders/${orderId}/documents/${type}`))
  } catch {
    giveUp()
    return 'failed'
  }
  if (res.ok && res.headers.get('content-type')?.includes('pdf')) {
    let pdf: Blob
    try {
      pdf = await res.blob()
    } catch {
      giveUp()
      return 'failed'
    }
    return deliverDocument(pdf, `order-${orderId}-${type}.pdf`, action)
  }
  giveUp()
  const code = await res
    .json()
    .then((body) => body?.detail?.code as string | undefined)
    .catch(() => undefined)
  return code === 'no_label' ? 'no_label' : 'failed'
}

export type PreviewDocumentType = 'packing_slip' | 'label'
export type SampleCarrier = 'usps' | 'ups'

/** The settings section each preview overlays its unsaved dialog state onto. */
const PREVIEW_SECTION: Record<PreviewDocumentType, string> = {
  packing_slip: 'packing_slip',
  label: 'label_layout',
}

/**
 * Printing Setup → Preview: a sample order drawn with `unsaved` laid over the
 * saved options, so the PDF shows what Save would print. `carrier` picks which
 * template the sample label uses; USPS and UPS lay theirs out differently.
 * Also the order page's "Print a test label" (with no unsaved options).
 */
export async function openPrintingPreview(
  documentType: PreviewDocumentType,
  unsaved: Record<string, unknown>,
  carrier?: SampleCarrier,
): Promise<DeliveredAs | 'failed'> {
  let res: Response
  try {
    res = await fetch(apiUrl(`/settings/printing/preview/${documentType}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        [PREVIEW_SECTION[documentType]]: unsaved,
        ...(carrier ? { carrier } : {}),
      }),
    })
  } catch {
    return 'failed'
  }
  if (res.ok && res.headers.get('content-type')?.includes('pdf')) {
    let pdf: Blob
    try {
      pdf = await res.blob()
    } catch {
      // The body can still break after a 200; the caller must hear 'failed'.
      return 'failed'
    }
    const filename = `${carrier ? `${carrier}-` : ''}${documentType}-preview.pdf`
    return deliverDocument(pdf, filename, 'print')
  }
  return 'failed'
}
